// IssueDurationTag — the duration-in-state indicator rendered beside the
// status pill. The pill itself already signals the state's loudness, so this
// tag starts as plain neutral text and only escalates to a coloured "▲ Nd"
// warn/alert once an issue has been genuinely STUCK in a state past an
// org-configurable threshold. Thresholds are deliberately high so the pill
// colour carries the early signal and the tag escalation marks the rare
// stuck case — stopping two signals fighting on one row.
//
// Only the two in-flight states carry thresholds: Awaiting response and In
// progress. Open / Resolved / Closed never escalate (their pill colour, or
// terminal nature, already says enough). The ▲ glyph reuses the brand's
// DeliverableSlipTag visual form; an aria-label carries the real semantics
// so screen readers don't verbalise the raw triangle.

import { clsx } from "clsx";
import type { IssueStatus } from "../../../../types";

export interface IssueDurationTagProps {
  status: IssueStatus;
  /** Whole days the issue has sat in its current status (derived on read). */
  durationDays: number;
}

interface Thresholds {
  /** Strictly-above this → amber warn. */
  warn: number;
  /** Strictly-above this → red alert. */
  alert: number;
}

// Org-configurable later; hardcoded to the proposal defaults for now. Set
// high on purpose (see module comment). Statuses absent from this map never
// escalate.
const STATUS_THRESHOLDS: Partial<Record<IssueStatus, Thresholds>> = {
  awaiting_response: { warn: 30, alert: 60 },
  in_progress: { warn: 21, alert: 45 },
};

type Level = "neutral" | "warn" | "alert";

function levelFor(status: IssueStatus, durationDays: number): Level {
  const t = STATUS_THRESHOLDS[status];
  if (!t) return "neutral";
  if (durationDays > t.alert) return "alert";
  if (durationDays > t.warn) return "warn";
  return "neutral";
}

const LEVEL_CLASS: Record<Level, string> = {
  neutral: "text-slate-500 dark:text-slate-400",
  warn: "text-amber-700 font-semibold dark:text-amber-400",
  alert: "text-red-700 font-semibold dark:text-red-400",
};

export default function IssueDurationTag({
  status,
  durationDays,
}: IssueDurationTagProps) {
  const level = levelFor(status, durationDays);
  const escalated = level !== "neutral";
  // Labelled ("in this state") so the count reads as "how long in the CURRENT
  // status" and groups with the status pill — distinct from the Age column's
  // total-since-raised. The ▲ escalation prefix still fires past the stuck
  // threshold.
  const text = `${escalated ? "▲ " : ""}${durationDays}d in this state`;
  // aria-label mirrors the visible "in this state" wording so the accessible
  // name matches what sighted users read.
  const ariaLabel = escalated
    ? `${durationDays} days in this state — overdue for attention`
    : `${durationDays} days in this state`;
  return (
    <span
      data-testid="issue-duration-tag"
      aria-label={ariaLabel}
      className={clsx(
        "tabular-nums text-caption",
        LEVEL_CLASS[level],
      )}
    >
      {text}
    </span>
  );
}
